import { Head } from "@inertiajs/react"
import { type AnimationEvent, useState } from "react"

import { ActivateAgentModal } from "@/components/landing/activate-agent-modal"
import {
  AssessmentOverlay,
  type AssessmentPurchaseContext,
} from "@/components/landing/discovery/assessment-overlay"
import { startAssessment } from "@/components/landing/discovery/use-assessment-lookup"
import { Faq } from "@/components/landing/hub/faq"
import { Features } from "@/components/landing/hub/features"
import { FinalCta } from "@/components/landing/hub/final-cta"
import { Footer } from "@/components/landing/hub/footer"
import { Hero } from "@/components/landing/hub/hero"
import { JobBlocks } from "@/components/landing/hub/job-blocks"
import { SiteHeader } from "@/components/landing/hub/site-header"
import { type JobOffer, type PartnerInfo } from "@/components/landing/hub/types"
import { useMeshSway } from "@/components/landing/hub/use-mesh-sway"
import { useForceLightMode } from "@/hooks/use-appearance"
import { assistantTheme } from "@/lib/assistant"
import {
  partnerLandingPageCheckoutIntentPath,
  partnerLandingPageFulfillPath,
  partnerLandingPageStartTrialPath,
  partnerLandingPageTrialSetupIntentPath,
} from "@/routes"

interface StripeInfo {
  publishable_key: string
  connected_account_id: string
}

// IMP-498: the partner's hub landing page — one page merchandising all three
// front-office jobs to the partner's SMB audience, under the partner's brand.
// Any prospect can self-serve purchase; there's no account yet (it's created at
// fulfillment), so each job block opens that job's purchasing modal.
export default function PartnerLandingPage({
  partner,
  jobs,
  stripe,
  landing_uid,
}: {
  partner: PartnerInfo
  jobs: JobOffer[]
  stripe: StripeInfo | null
  landing_uid: string
}) {
  // IMP-292: light-only marketing page — pin it out of OS-driven dark mode so
  // the purchasing modal (portaled onto <body>) stays light too.
  useForceLightMode()
  useMeshSway()

  const [active, setActive] = useState<JobOffer | null>(null)
  // IMP-502: the open assessment; null keeps the overlay unmounted so each
  // run starts fresh. IMP-503 follow-up: the record is created at URL-submit
  // time — the server's pre-screen resolves at the field, so by the time the
  // overlay opens the assessment uid already exists.
  const [assessing, setAssessing] = useState<{
    website: string
    assessmentUid: string
  } | null>(null)

  async function handleTrain(
    website: string,
    company: string,
  ): Promise<string | null> {
    const result = await startAssessment(landing_uid, website, company)
    if ("error" in result) return result.error
    setAssessing({ website, assessmentUid: result.assessmentUid })
    return null
  }
  // IMP-504: what the assessment knew when Get started was tapped. With an
  // account landing uid the purchase runs the expedited existing-account
  // checkout (/lp/:uid — the receptionist trained during the assessment is
  // the agent being activated); without one it falls back to the general
  // partner checkout that creates the account at fulfillment.
  const [assessmentContext, setAssessmentContext] =
    useState<AssessmentPurchaseContext | null>(null)

  // IMP-297: partner-managed partners activate through the partner, not the
  // self-serve Stripe flow. With no Stripe either, nothing on this page can
  // transact — the job blocks drop their Hire actions and the closing panel's
  // Contact us carries the gesture.
  const sellable = !partner.partner_managed && stripe != null
  const offers: Record<string, JobOffer> = {}
  for (const job of jobs) {
    offers[job.slug] = sellable ? job : { ...job, pricing: null }
  }

  // Every accent on the page derives from the partner's agent identity (the
  // colour set in Settings → Identity), so the hub reads in the same brand
  // colour their clients meet inside the product.
  const brand = assistantTheme(partner.assistant_color).swatch

  const pricing = active?.pricing ?? null

  // IMP-502: the entrance animation's fill mode would leave a filter on the
  // page root forever, and a filtered ancestor becomes the containing block
  // for every position: fixed descendant — the assessment overlay and its
  // pinned bars would anchor to the document instead of the viewport. Clear
  // the animation once it has played so the root goes back to being inert.
  function clearEntranceAnimation(event: AnimationEvent<HTMLDivElement>) {
    if (event.animationName !== "lp-page-in") return
    if (event.target !== event.currentTarget) return
    event.currentTarget.style.animation = "none"
  }

  return (
    <>
      <Head title={partner.name}>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin=""
        />
        <link
          href="https://fonts.googleapis.com/css2?family=Ovo&family=Inter:wght@400;500;600&display=swap"
          rel="stylesheet"
        />
      </Head>

      <div
        id="top"
        className="lp-root lp-page-in bg-lp-bg font-lp-sans text-lp-fg min-h-screen antialiased"
        style={{ "--lp-brand": brand } as React.CSSProperties}
        onAnimationEnd={clearEntranceAnimation}
      >
        <SiteHeader partner={partner} />
        <Hero partner={partner} onTrain={handleTrain} />
        <JobBlocks
          offers={offers}
          onHire={(job) => {
            setAssessmentContext(null)
            setActive(job)
          }}
        />
        <Features />
        <Faq />
        <FinalCta onTrain={handleTrain} />
        <Footer partner={partner} />

        {/* IMP-502: the free assessment, launched from the hero or final-CTA
            URL field. Inside .lp-root so the partner accent tokens reach it. */}
        {assessing !== null && (
          <AssessmentOverlay
            landingUid={landing_uid}
            website={assessing.website}
            assessmentUid={assessing.assessmentUid}
            partner={partner}
            jobs={Object.values(offers)}
            onGetStarted={(job, context) => {
              setAssessmentContext(context)
              setActive(job)
            }}
            onClose={() => setAssessing(null)}
          />
        )}
      </div>

      {active &&
        pricing &&
        stripe &&
        (() => {
          // IMP-504: an assessment that already created its account routes a
          // receptionist purchase through that account's own landing-page
          // checkout — the modal's default endpoints — so the provisional
          // receptionist it trained is the agent being activated. Other jobs
          // (and pre-account purchases) use the general partner checkout.
          const expeditedUid =
            active.slug === "receptionist"
              ? (assessmentContext?.accountLandingUid ?? null)
              : null
          const pathOverrides = expeditedUid
            ? {}
            : {
                checkoutIntentPath:
                  partnerLandingPageCheckoutIntentPath(landing_uid),
                fulfillPath: partnerLandingPageFulfillPath(landing_uid),
                trialSetupIntentPath:
                  partnerLandingPageTrialSetupIntentPath(landing_uid),
                startTrialPath: partnerLandingPageStartTrialPath(landing_uid),
              }
          return (
            <ActivateAgentModal
              // Remount per job: Stripe Elements is configured with the amount
              // at mount, so switching jobs has to rebuild it.
              key={active.slug}
              open
              onOpenChange={(next) => {
                if (!next) setActive(null)
              }}
              landingUid={expeditedUid ?? landing_uid}
              {...pathOverrides}
              archetypeSlug={active.slug}
              agentName={active.name}
              productLabel={active.name}
              retailPriceCents={pricing.retail_price_cents}
              currency={pricing.currency}
              includedMinutes={pricing.included_minutes}
              overageRetailPerMinute={pricing.overage_retail_per_minute}
              trial={pricing.trial ?? null}
              publishableKey={stripe.publishable_key}
              connectedAccountId={stripe.connected_account_id}
              defaultEmail={assessmentContext?.email ?? undefined}
            />
          )
        })()}
    </>
  )
}
